import type { PublicLoadout } from './events.js';
import {
  addCalendarDays,
  calendarWeekday,
  eachCalendarDate,
  isCalendarDate,
  shiftYearMonth,
} from './availability.js';

/** Stored request statuses (F-24). One row per event and user. */
export const ASSIGNMENT_REQUEST_STATUSES = [
  'requested',
  'declined',
  'accepted',
  'withdrawn',
] as const;
export type AssignmentRequestStatus = (typeof ASSIGNMENT_REQUEST_STATUSES)[number];

/** What the availability row shows. Assigned is computed from the crew, not the row. */
export const ASSIGNMENT_REQUEST_DISPLAY_STATES = [
  'none',
  'requested',
  'declined',
  'assigned',
] as const;
export type AssignmentRequestDisplayState = (typeof ASSIGNMENT_REQUEST_DISPLAY_STATES)[number];

export function isAssignmentRequestStatus(value: unknown): value is AssignmentRequestStatus {
  return (
    typeof value === 'string' && (ASSIGNMENT_REQUEST_STATUSES as readonly string[]).includes(value)
  );
}

export interface OperationalWeekend {
  friday: string;
  saturday: string;
  sunday: string;
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Friday–Sunday windows that have at least one day in the calendar month.
 * A weekend that sits entirely in a neighbor is omitted. Leading and trailing
 * grid days do not add a second month.
 */
export function weekendsTouchingMonth(year: number, month: number): OperationalWeekend[] {
  const first = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`;
  if (!isCalendarDate(first)) throw new Error(`Invalid month ${year}-${month}.`);
  const next = shiftYearMonth(year, month, 1);
  const nextFirst = `${String(next.year).padStart(4, '0')}-${String(next.month).padStart(2, '0')}-01`;
  const last = addCalendarDays(nextFirst, -1);
  const weekends: OperationalWeekend[] = [];
  for (const date of eachCalendarDate(addCalendarDays(first, -2), addCalendarDays(last, 2))) {
    if (calendarWeekday(date) !== 5) continue;
    const saturday = addCalendarDays(date, 1);
    const sunday = addCalendarDays(date, 2);
    const touches = [date, saturday, sunday].some((day) => {
      const parts = day.split('-').map(Number);
      return parts[0] === year && parts[1] === month;
    });
    if (!touches) continue;
    weekends.push({ friday: date, saturday, sunday });
  }
  return weekends;
}

export function formatMonthDay(ymd: string): string {
  const parts = ymd.split('-').map(Number);
  const month = parts[1];
  const day = parts[2];
  const label = month === undefined ? undefined : SHORT_MONTHS[month - 1];
  if (!label || day === undefined) throw new Error(`Invalid calendar date "${ymd}".`);
  return `${label} ${day}`;
}

export function formatDateSpan(start: string, end: string): string {
  if (start === end) return formatMonthDay(start);
  return `${formatMonthDay(start)} – ${formatMonthDay(end)}`;
}

function sameDates(dates: readonly string[], expected: readonly string[]): boolean {
  if (dates.length !== expected.length) return false;
  const set = new Set(expected);
  return dates.every((date) => set.has(date));
}

/** Day text for one row, judged against that row's weekend — not the whole month. */
export function weekendDayText(
  startDate: string,
  endDate: string,
  weekend: OperationalWeekend,
): string {
  const dates = eachCalendarDate(startDate, endDate);
  const thursday = addCalendarDays(weekend.friday, -1);
  const monday = addCalendarDays(weekend.sunday, 1);
  const { friday, saturday, sunday } = weekend;
  if (sameDates(dates, [friday, saturday, sunday])) return 'Fri–Sun';
  if (sameDates(dates, [friday])) return 'Fri only';
  if (sameDates(dates, [saturday])) return 'Sat only';
  if (sameDates(dates, [sunday])) return 'Sun only';
  if (sameDates(dates, [friday, saturday])) return 'Fri–Sat';
  if (sameDates(dates, [saturday, sunday])) return 'Sat–Sun';
  if (sameDates(dates, [thursday, friday, saturday, sunday])) return 'Thu–Sun';
  if (sameDates(dates, [friday, saturday, sunday, monday])) return 'Fri–Mon';
  return formatDateSpan(startDate, endDate);
}

export function tournamentPlace(
  locationText: string | null | undefined,
  organizationName: string | null | undefined,
): string | null {
  const location = locationText?.trim() ?? '';
  if (location.length > 0) return location;
  const organization = organizationName?.trim() ?? '';
  return organization.length > 0 ? organization : null;
}

export function joinPlaceAndDays(place: string | null, dayText: string): string {
  return place ? `${place} · ${dayText}` : dayText;
}

export function eventOverlapsWeekend(
  event: { startDate: string; endDate: string },
  weekend: OperationalWeekend,
): boolean {
  return event.startDate <= weekend.sunday && event.endDate >= weekend.friday;
}

/** Ended means the end date is before today in America/Chicago. The end date itself is not ended. */
export function eventHasEnded(endDate: string, today: string): boolean {
  return endDate < today;
}

export type RequestRejection = 'unconfirmed' | 'scratch' | 'completed' | 'ended';

export function requestRejectionReason(
  event: { confirmed: boolean; status: string; endDate: string },
  today: string,
): RequestRejection | null {
  if (!event.confirmed) return 'unconfirmed';
  if (event.status === 'scratch') return 'scratch';
  if (event.status === 'completed') return 'completed';
  if (eventHasEnded(event.endDate, today)) return 'ended';
  return null;
}

export function decisionRejectionReason(event: { status: string }): 'scratch' | 'completed' | null {
  if (event.status === 'scratch') return 'scratch';
  if (event.status === 'completed') return 'completed';
  return null;
}

/**
 * Assigned wins. An accepted or withdrawn row with no crew seat is no request.
 * Declined stays declined until they ask again.
 */
export function assignmentRequestDisplayState(
  onCrew: boolean,
  status: AssignmentRequestStatus | null,
): AssignmentRequestDisplayState {
  if (onCrew) return 'assigned';
  if (status === 'requested') return 'requested';
  if (status === 'declined') return 'declined';
  return 'none';
}

export interface TournamentSourceEvent {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  confirmed: boolean;
  status: string;
  locationText: string | null;
  organizationName: string | null;
  loadoutId: string;
}

export interface TournamentListRow {
  eventId: string;
  name: string;
  place: string | null;
  dayText: string;
  detail: string;
  startDate: string;
  endDate: string;
  state: AssignmentRequestDisplayState;
}

export interface TournamentWeekend {
  friday: string;
  saturday: string;
  sunday: string;
  events: TournamentListRow[];
}

export interface TournamentMonthResponse {
  year: number;
  month: number;
  userId: string;
  weekends: TournamentWeekend[];
}

function isListedTournament(event: TournamentSourceEvent): boolean {
  return event.confirmed && event.status !== 'scratch' && event.status !== 'completed';
}

function compareTournamentEvents(a: TournamentSourceEvent, b: TournamentSourceEvent): number {
  if (a.startDate !== b.startDate) return a.startDate < b.startDate ? -1 : 1;
  const byName = a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' });
  if (byName !== 0) return byName;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

export function listTournamentsForMonth(
  year: number,
  month: number,
  events: readonly TournamentSourceEvent[],
  lookup: (event: TournamentSourceEvent) => {
    onCrew: boolean;
    requestStatus: AssignmentRequestStatus | null;
  },
): TournamentWeekend[] {
  return weekendsTouchingMonth(year, month).map((weekend) => {
    const rows = events
      .filter((event) => isListedTournament(event) && eventOverlapsWeekend(event, weekend))
      .slice()
      .sort(compareTournamentEvents)
      .map((event) => {
        const place = tournamentPlace(event.locationText, event.organizationName);
        const dayText = weekendDayText(event.startDate, event.endDate, weekend);
        const crew = lookup(event);
        return {
          eventId: event.id,
          name: event.name,
          place,
          dayText,
          detail: joinPlaceAndDays(place, dayText),
          startDate: event.startDate,
          endDate: event.endDate,
          state: assignmentRequestDisplayState(crew.onCrew, crew.requestStatus),
        };
      });
    return { ...weekend, events: rows };
  });
}

export interface AssignmentRequestResponse {
  eventId: string;
  userId: string;
  status: AssignmentRequestStatus;
}

export interface OpenAssignmentRequest {
  userId: string;
  name: string;
}

export interface AssignmentRequestListResponse {
  requests: OpenAssignmentRequest[];
}

export interface AssignmentRequestDecisionResponse {
  eventId: string;
  userId: string;
  status: 'accepted' | 'declined';
  loadout?: PublicLoadout;
}
